<template lang="pug">
transition(name="modal")
  .swapcard-login-modal(
    v-if="visible"
    role="dialog"
    aria-modal="true"
    aria-labelledby="swapcard-modal-title")
    .swapcard-login-modal__overlay(@click="close")
    .swapcard-login-modal__dialog(ref="dialog" @click.stop="")
      button.swapcard-login-modal__close(
        type="button"
        @click="close"
        aria-label="Close modal")
        span(aria-hidden="true") &times;

      .swapcard-login-modal__content
        .swapcard-login-modal__icon
          svg(viewBox="0 0 24 24" width="40" height="40" fill="none" aria-hidden="true")
            path(d="M12 1C9.24 1 7 3.24 7 6v2H5a2 2 0 00-2 2v11a2 2 0 002 2h14a2 2 0 002-2V10a2 2 0 00-2-2h-2V6c0-2.76-2.24-5-5-5zm0 2a3 3 0 013 3v2H9V6a3 3 0 013-3zm0 9a2 2 0 110 4 2 2 0 010-4z" fill="currentColor")

        h2#swapcard-modal-title.swapcard-login-modal__title Log In To Continue to TASB Events
        p.swapcard-login-modal__subtitle Manage your personal schedule and download materials.
        p.swapcard-login-modal__message You'll be redirected to TASB Events to sign in.

        .swapcard-login-modal__actions
          a.swapcard-login-modal__login-btn(
            :href="resolvedLoginUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click="close") Log In

          a.swapcard-login-modal__download-btn(
            v-if="iosDownloadUrl"
            :href="iosDownloadUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click="close")
            span Download for iOS
            svg.swapcard-login-modal__phone-icon(viewBox="0 0 11 16" width="11" height="16" fill="none" aria-hidden="true")
              rect(x="0.75" y="0.75" width="9.5" height="14.5" rx="1.5" stroke="currentColor" stroke-width="1.5")
              circle(cx="5.5" cy="13" r="0.75" fill="currentColor")

          a.swapcard-login-modal__download-btn(
            v-if="androidDownloadUrl"
            :href="androidDownloadUrl"
            target="_blank"
            rel="noopener noreferrer"
            @click="close")
            span Download for Android
            svg.swapcard-login-modal__phone-icon(viewBox="0 0 11 16" width="11" height="16" fill="none" aria-hidden="true")
              rect(x="0.75" y="0.75" width="9.5" height="14.5" rx="1.5" stroke="currentColor" stroke-width="1.5")
              circle(cx="5.5" cy="13" r="0.75" fill="currentColor")

        .swapcard-login-modal__cancel
          a.swapcard-login-modal__cancel-link(
            href="#"
            @click.prevent="close") Cancel and Return
</template>

<script>
export default {
  name: 'SwapcardLoginModal',

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loginUrl: {
      type: String,
      default: '',
    },
  },

  computed: {
    resolvedLoginUrl() {
      if (this.loginUrl) return this.loginUrl;
      const slug = this.$root.eventSlug || '';
      if (!slug) return '#';
      return `https://myevents.tasb.org/login?redirect_url=${encodeURIComponent(`https://myevents.tasb.org/event/${slug}`)}`;
    },

    iosDownloadUrl() {
      return this.$root.swapcardAppDownloadUrlIos || '';
    },

    androidDownloadUrl() {
      return this.$root.swapcardAppDownloadUrlAndroid || '';
    },
  },

  watch: {
    visible(isVisible) {
      if (isVisible) {
        this._focusTrigger = document.activeElement;
        document.addEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => {
          const focusable = this.$refs.dialog && this.$refs.dialog.querySelector('a[href], button');
          if (focusable) focusable.focus();
        });
      } else {
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = '';
        if (this._focusTrigger) {
          this._focusTrigger.focus();
          this._focusTrigger = null;
        }
      }
    },
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  },

  methods: {
    close() {
      this.$emit('close');
    },

    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab' && this.$refs.dialog) {
        const focusable = Array.from(
          this.$refs.dialog.querySelectorAll('a[href], button:not([disabled])')
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

// ---------- Overlay & positioning ----------
.swapcard-login-modal {
  font-family: 'Public Sans', sans-serif;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.swapcard-login-modal__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.55);
}

// ---------- Dialog box ----------
.swapcard-login-modal__dialog {
  position: relative;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 480px;
  width: 90%;
  padding: 2rem 2rem 1.5rem;
  z-index: 1;
}

// ---------- Close button (X) ----------
.swapcard-login-modal__close {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  font-size: 1.6rem;
  line-height: 1;
  color: $color-text-secondary;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: $color-text-dark;
    background: $color-border;
  }
}

// ---------- Content ----------
.swapcard-login-modal__content {
  text-align: center;
}

.swapcard-login-modal__icon {
  color: $color-text-secondary;
  margin-bottom: 0.75rem;
}

.swapcard-login-modal__title {
  font-size: 1.35rem;
  font-weight: 700;
  color: $color-primary;
  margin: 0 0 0.5rem;
}

.swapcard-login-modal__subtitle {
  font-size: 0.95rem;
  font-weight: 600;
  color: $color-text-dark;
  margin: 0 0 0.5rem;
}

.swapcard-login-modal__message {
  font-size: 0.95rem;
  line-height: 1.5;
  color: $color-text-secondary;
  margin: 0 0 1.5rem;
}

// ---------- Action buttons ----------
.swapcard-login-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  margin-bottom: 1.25rem;
}

.swapcard-login-modal__login-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 324px;
  padding: 0.7rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: $color-cta-text;
  background: $color-cta-accent;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: $color-cta-accent-hover;
    text-decoration: none;
    color: $color-cta-text;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.swapcard-login-modal__download-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 324px;
  padding: 0.7rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: $color-text-dark;
  background: #fff;
  border: 1px solid $color-primary;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
    text-decoration: none;
    color: $color-text-dark;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.swapcard-login-modal__phone-icon {
  flex-shrink: 0;
}

// ---------- Cancel link ----------
.swapcard-login-modal__cancel {
  text-align: center;
}

.swapcard-login-modal__cancel-link {
  font-size: 0.9rem;
  color: $color-text-secondary;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-text-dark;
  }
}

// ---------- Transition ----------
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .swapcard-login-modal__dialog,
.modal-leave-active .swapcard-login-modal__dialog {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

.modal-enter .swapcard-login-modal__dialog,
.modal-leave-to .swapcard-login-modal__dialog {
  transform: scale(0.95) translateY(-10px);
  opacity: 0;
}
</style>
